import { inject, Injectable } from '@angular/core';
import { ProjectModel } from '../model/project.model';
import { ArticleDataService } from '@article/service/article-data.service';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { lastValueFrom } from 'rxjs';
import { ArticleVO } from '@article/model/vo/article.vo';
import { range } from 'lodash';
import { AddArticleService } from '@configurator/project/add-article.service';
import { SuitableConnectionAreasVO } from '@project/components/article/model/vo/suitable-connection-areas.vo';
import { ArticleUtils } from '@article/util/article-utils';

const LOCKING_PIN_ARTICLE_NO = '274-1001';

@Injectable({
  providedIn: 'root'
})
export class LockingPinService {

  private readonly projectModel = inject(ProjectModel);
  private readonly articleDataService = inject(ArticleDataService);
  private readonly containerService = inject(ContainerService);
  private readonly addArticleService = inject(AddArticleService);

  public async addLockingPinsToArticles(articleGuids: string[]): Promise<void> {
    const articleContainers = this.getArticleContainersWithPinConnections(articleGuids);
    for (let i=0; i < articleContainers.length - 1; i+=2) {
      const pinConnections = this.getPinConnectionsForArticles(articleContainers[i], articleContainers[i + 1]);
      const pinArticles = await lastValueFrom(this.articleDataService.getLatestArticlesByNoObservable([LOCKING_PIN_ARTICLE_NO]));
      await this.addPin(pinArticles, pinConnections);
    }
  }

  private getArticleContainersWithPinConnections(articleGuids: string[]): ArticleContainer[] {
    let positionsToAddPins: number[];
    switch (articleGuids.length) {
      case 2:
      case 4:
        positionsToAddPins = range(0, articleGuids.length);
        break;
      case 3:
        positionsToAddPins = [0, 1, 1, 2];
        break;
      case 5:
        positionsToAddPins = [0, 1, 3, 4];
        break;
      case 6:
        positionsToAddPins = [0, 1, 4, 5];
        break;
      case 7:
        positionsToAddPins = [0, 1, 2, 3, 5, 6];
        break;
      case 8:
        positionsToAddPins = [0, 1, 3, 4, 6, 7];
        break;
      default:
        positionsToAddPins = [];
    }
    const articleGuidsToAddPins = positionsToAddPins.map(index => articleGuids[index]);
    const articleContainers = articleGuidsToAddPins.map(articleGuid => this.containerService.findArticleContainerInGlobal(articleGuid));
    return articleContainers.filter(container => container?.connections.some(LockingPinService.isPinConnection) ?? false);
  }

  private static isPinConnection(connection: Connection) {
    if (connection.metaElementVO instanceof ConnectionAreaVO) {
      return connection.metaElementVO.connectionRequirements
        .some(requirement => {
          const hasTypePin = requirement.name === 'Typ'
            && requirement.values.some(value => value.stringValue === 'PIN');
          return hasTypePin;
        });
    }
  }

  private getPinConnectionsForArticles(leftArticleContainer: ArticleContainer, rightArticleContainer: ArticleContainer): (Connection | undefined)[] {
    const leftPinConnection = ConnectionInfoUtils.sortConnections(leftArticleContainer.connections.filter(LockingPinService.isPinConnection), true).at(1);
    const rightPinConnection = ConnectionInfoUtils.sortConnections(rightArticleContainer.connections.filter(LockingPinService.isPinConnection), true).at(0);
    return [ leftPinConnection, rightPinConnection ];
  }

  private async addPin(articles: ArticleVO[], connections: (Connection | undefined)[]): Promise<boolean> {
    if (connections.every(c => c !== undefined) && this.projectModel.currentProjectType && articles.length > 0) {
      const suitableConnectionAreas = SuitableConnectionAreasVO.create(articles[0], ArticleUtils.givingAreas(articles[0]));
      const articleContainers = await this.addArticleService.addArticleToConnections(suitableConnectionAreas, connections);
      return articleContainers.length > 0;
    }
    return false;
  }
}
